import React, { useEffect, useState } from "react";
import {
  SimpleForm,
  Edit,
  required,
  TextInput,
  SelectInput,
  ImageInput,
  ImageField,
  AutocompleteInput,
  FormDataConsumer,
  useRecordContext,
  DateTimeInput,
  useGetOne,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  DeleteWithConfirmButton,
  useRefresh,
} from "react-admin";
import { makeStyles } from "@mui/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import mbaas from "../../../provider/Mbaas";
import { dataProvider } from "../../../provider";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useRetrievePermission } from "../../../hooks/useRetrievePermission";

const countryList = [
  { id: "HK", name: "Hong Kong" },
  { id: "ID", name: "Indonesia" },
  { id: "MY", name: "Malaysia" },
  { id: "TW", name: "Taiwan" },
  { id: "WORLDWIDE", name: "WORLDWIDE" },
];

const accountType = [
  { id: "M21", name: "M21" },
  { id: "G2", name: "G2" },
  { id: "GHK_PREPAID", name: "GHK - Prepaid" },
  { id: "GHK_POSTPAID", name: "GHK - Postpaid" },
];

const validateDates = (values: any) => {
  const errors: any = {};
  if (
    values.date_end &&
    values.date_start &&
    new Date(values.date_end) <= new Date(values.date_start)
  ) {
    errors.date_end = "End date must be after start date";
  }
  return errors;
};

const PrevImage = () => {
  const record: any = useRecordContext();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "left",
        flexDirection: "row",
        marginBlockEnd: 50,
      }}
    >
      <p>Previous Image</p>
      <img src={record?.image} width={200} alt="img"></img>
    </div>
  );
};

const ToolbarEdit = () => {
  return (
    <Toolbar>
      <SaveButton sx={{ marginRight: "20px" }} />
      <DeleteWithConfirmButton redirect={`/promotionsPopup`} />
    </Toolbar>
  );
};

const EditPage = (props: any) => {
  const classes = useStyles();
  const { id } = useParams();
  const [isSuperAdmin, isAdmin] = useRetrievePermission();

  const { data, isLoading: loadingGetOne } = useGetOne(
    "promotionsPopup",
    { id: id },
    { enabled: !!id }
  );

  const getLinkToList = async (country: string, platform: string[]) => {
    const resp = await mbaas.client.invokeApi("console-webview-link-list", {
      country: country,
      platform: platform,
    });

    if (resp.error) {
      return [{ url: "custom_url", label: "CUSTOM URL" }];
    }
    resp.data.push({ url: "custom_url", label: "CUSTOM URL" });
    return resp.data;
  };

  const [country, setCountry] = useState(data?.country);
  const [platform, setPlatform] = useState(data?.account_type);
  const [link, setLink] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchLink = async () => {
      try {
        const inputPlatform = ["GHK_PREPAID", "GHK_POSTPAID"].includes(platform)
          ? "GHK"
          : platform;
        const data = await getLinkToList(country, [inputPlatform]);
        if (isMounted) setLink(data);
      } catch (error) {
        console.error("Error fetching links:", error);
      }
    };
    fetchLink();
    return () => {
      isMounted = false;
    };
  }, [country, platform]);

  const redirect = useRedirect();
  const refresh = useRefresh();
  const notify = useNotify();

  const onSubmitHandler = async (formData: any) => {
    await dataProvider
      .update("promotionsPopup", {
        data: {
          ...formData,
        },
        id: props.id,
        previousData: data,
      })
      .then(() => {
        notify("ra.notification.created", {
          type: "info",
          messageArgs: { smart_count: 1 },
          undoable: true,
        });
        redirect(`/promotionsPopup`);
        refresh();
        Swal.fire("Success!", "", "success");
      });
  };

  return (
    <Edit title=" " {...props} component="div">
      <Box ml={2} mr={2}>
        <SimpleForm
          toolbar={isSuperAdmin || isAdmin ? <ToolbarEdit /> : false}
          onSubmit={isSuperAdmin || isAdmin ? onSubmitHandler : undefined}
          validate={validateDates}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <SelectInput
                fullWidth
                className={classes.input}
                source="country"
                validate={[required()]}
                choices={countryList}
                label="Country"
                data-test-id="country"
                onChange={(e) => {
                  setCountry(e.target.value);
                }}
                disabled={!(isSuperAdmin || isAdmin)}
              />
            </Grid>
            <Grid item xs={6}>
              <AutocompleteInput
                fullWidth
                className={classes.input}
                source="account_type"
                validate={[required()]}
                choices={accountType}
                optionText="name"
                optionValue="id"
                label="Account Type"
                data-test-id="account_type"
                onChange={(value) => {
                  setPlatform(value);
                }}
                disabled={!(isSuperAdmin || isAdmin)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="title"
                validate={[required()]}
                multiline
                label="Title"
                fullWidth
                data-test-id="title"
                disabled={!(isSuperAdmin || isAdmin)}
              />
            </Grid>
            <Grid item xs={12}>
              <AutocompleteInput
                source="url"
                label="Link to"
                optionText="label"
                optionValue="url"
                choices={link}
                fullWidth
                validate={required()}
                data-test-id="uri"
                disabled={!(isSuperAdmin || isAdmin)}
              />
            </Grid>
            <FormDataConsumer>
              {({ formData }: any) =>
                formData.url === "custom_url" && (
                  <Grid item xs={12}>
                    <TextInput
                      fullWidth
                      source="custom_url"
                      label="Custom Url"
                      validate={[
                        required(),
                        (value) =>
                          value && value.startsWith("https://")
                            ? undefined
                            : "Must start with https://",
                      ]}
                      disabled={!(isSuperAdmin || isAdmin)}
                    />
                  </Grid>
                )
              }
            </FormDataConsumer>
            <Grid item xs={6}>
              <DateTimeInput
                fullWidth
                className={classes.input}
                source="date_start"
                label="Valid From"
                defaultValue={new Date()}
                parse={(v) => new Date(v)}
                validate={required()}
                disabled={!(isSuperAdmin || isAdmin)}
              />
            </Grid>
            <Grid item xs={6}>
              <DateTimeInput
                fullWidth
                className={classes.input}
                source="date_end"
                label="Valid Until"
                defaultValue={new Date()}
                parse={(v) => new Date(v)}
                validate={required()}
                disabled={!(isSuperAdmin || isAdmin)}
              />
            </Grid>
            <Grid
              item
              xs={12}
              style={{
                display: "flex",
                justifyContent: "flex-start",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <PrevImage />
              <ImageInput
                data-test-id="image"
                source="urlThumbnail"
                label="Main Image (.jpg)"
                validate={[
                  (value) => {
                    if (value && value.rawFile) {
                      const file = value.rawFile;
                      if (file.type !== "image/jpeg") {
                        return "Only JPG images are allowed";
                      }
                      if (file.size > 400 * 1024) {
                        return "Image size must be less than 400KB";
                      }
                    }
                    return undefined;
                  },
                ]}
                options={{ multiple: false }}
                // @ts-expect-error react-admin's ImageInputProps type omits disabled, though it is forwarded to useInput at runtime
                disabled={!(isSuperAdmin || isAdmin)}
              >
                <ImageField source="src" title="title" />
              </ImageInput>
            </Grid>
          </Grid>
        </SimpleForm>
      </Box>
    </Edit>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
  },
  form: {
    boxShadow: "none",
  },
  input: {
    marginBottom: "-20px ",
  },
  text: {
    color: "#0000008A",
  },
}));

export default EditPage;
